import isEmpty from 'lodash/isEmpty';
import appHelpers from 'app/app-helpers';
import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { cancelIconsChanges, discardIcons, fetchIcons, uploadIcon } from 'app/actions/manage-icons.actions';
import { checkAccess } from 'app/actions/app.actions';
import template from 'app/components/home/management/components/widgets/widgets-management.html';
import * as apiWidgets from 'app/resources/widgets.api';
import { cancelWidgetsChanges, deleteWidget, fetchWidgets } from 'app/actions/widgets.actions';

const SECOND_HAND_PERMISSION = 'cms.widgetsManagement.approve';
const ICONS_MANAGEMENT_PERMISSION = 'cms.iconsManagement';
export const DIRTY_FORM = 'widgetsManagement';

const mapStateToThis = ({ session, config, ui, manageIcons, widgets, dirtyForms }) => {
  const { locale } = session;
  const { locales, urlPattern, urlPatternError, maxWidgetsNumber, secondHandApproval, jurisdictionId } = config;
  const { selectedLanguage } = ui;
  const { iconsApprovalStatus, entries, error, notification, isFileUploading } = manageIcons;
  const { copyItems, draft, isLoading } = widgets;

  return {
    locale,
    locales,
    selectedLanguage,
    iconsApprovalStatus,
    entries,
    error,
    notification,
    isFileUploading,
    dirtyForm: dirtyForms[DIRTY_FORM],
    urlPattern,
    urlPatternError,
    maxWidgetsNumber,
    secondHandApproval,
    jurisdictionId,
    copyItems,
    draft,
    isLoading,
  };
};

const mapDispatchToThis = {
  setDirtyForm,
  uploadIcon,
  fetchIcons,
  cancelIconsChanges,
  discardIcons,
  fetchWidgets,
  cancelWidgetsChanges,
  deleteWidget,
  checkAccess,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', '$scope', '$sce', 'gettextCatalog', 'DirtyForm', 'Notifications')
class WidgetsManagementController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  async $onInit() {
    this.DIRTY_FORM = DIRTY_FORM;
    this.activeTabIdx = 'widgets';
    this.secondHandApprovalActive = this.checkAccess(SECOND_HAND_PERMISSION);
    this.iconsManagement = this.checkAccess(ICONS_MANAGEMENT_PERMISSION);
    this.asSortableOptions = {
      dragMove: appHelpers.dragMove,
      orderChanged: () => {
        this.setDirtyForm(this.DIRTY_FORM, true);
      },
    };
    if (!this.$state.params.skipReloadData || isEmpty(this.copyItems)) {
      await this.initWidgets();
    }
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async initWidgets() {
    await this.fetchWidgets();
    this.setWidgetEntries(this.locale);
  }

  setWidgetEntries(locale) {
    const key = Object.keys(this.copyItems).find((localeKey) => localeKey === locale);
    if (!key) {
      this.copyItems[locale] = {};
    }
  }

  onTabSelect(tabId) {
    this.DirtyForm.check(this.DIRTY_FORM, (dirty) => {
      if (dirty) {
        if (tabId === 'widgets') {
          this.cancelIconsChanges();
        } else if (tabId === 'icons') {
          this.cancelWidgetsChanges();
        }
      }
      this.activeTabIdx = tabId;
    });
  }

  discardWidgetsChanges() {
    return apiWidgets
      .discardWidgetsChanges(this.jurisdictionId)
      .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('Changes have been discarded')))
      .then(() => this.initWidgets())
      .then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  onCancelWidgetsChanges() {
    this.cancelWidgetsChanges();
    this.setDirtyForm(this.DIRTY_FORM, false);
  }

  discardIconsChanges() {
    return this.discardIcons().then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  onDeleteWidget(index) {
    this.deleteWidget(index);
    this.setDirtyForm(this.DIRTY_FORM, true);
  }

  publishWidgetsChanges() {
    return apiWidgets
      .publishWidgets(this.secondHandApproval, this.jurisdictionId, this.locales, this.copyItems)
      .then(() => this.Notifications.alertSuccess(this.getSuccessText()))
      .then(() => this.initWidgets())
      .then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  publishIconsChanges() {
    return this.uploadIcon().then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  getSuccessText() {
    return this.secondHandApprovalActive || !this.secondHandApproval
      ? this.gettextCatalog.getString('Changes have been published')
      : this.gettextCatalog.getString('Changes have been submitted for approval');
  }

  // eslint-disable-next-line class-methods-use-this
  onDownloadWidgetAttachment(widget) {
    return apiWidgets.downloadAttachment(widget);
  }

  isSubmitForApprovalAvailable() {
    return this.dirtyForm && this.secondHandApproval && !this.secondHandApprovalActive;
  }

  isDiscardWidgetsChangesAvailable() {
    return this.secondHandApproval && this.secondHandApprovalActive && this.draft;
  }

  isPublishWidgetsAvailable() {
    return (
      (!this.secondHandApproval && this.dirtyForm) ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.draft) ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.dirtyForm)
    );
  }

  isDiscardIconsChangesAvailable() {
    return this.secondHandApproval && this.secondHandApprovalActive && this.iconsApprovalStatus === 'DRAFT';
  }

  isPublishIconsAvailable() {
    return (
      (!this.secondHandApproval && this.dirtyForm) ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.iconsApprovalStatus === 'DRAFT') ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.dirtyForm)
    );
  }
}

export default class WidgetsManagementComponent {
  constructor() {
    this.controller = WidgetsManagementController;
    this.template = template;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/app/components/home/management/components/widgets/widgets-management-component.js